import { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { Header } from "../../common/header/Header"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { IReplenishmentListProjection } from "../../../types/replenishment/IReplenishmentListProjection"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { ReplenishmentStatus } from "../../../types/replenishment/ReplenishmentStatus"
import { IReplenishmentStatus } from "../../../types/replenishment/IReplenishmentStatus"
import { useStore } from "../../../stores/RootStoreContext"
import { MenuManager } from "../MenuManager"
import { MenuAdmin } from "../../admin/MenuAdmin"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { IMG } from "../../common/CONST"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import Input from "@/ui/components/Input"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import { ModalConfirm } from "@/components/common/element/ModalConfirm"

export const CurrencyManagerReplenishmentList = () => {
  const { userStore } = useStore()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [confirmMessage, setConfirmMessage] = useState<IModalMessage>()
  const [replenishments, setReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [search, setSearch] = useState("")
  const [filteredReplenishments, setFilteredReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [status, setStatus] = useState("")

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "CURREPLENISHMENT"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  /* EOF MESSENGER FCN */

  const fetchReplenishments = () => {
    setSearch("")

    TrinityAPI.getCurrencyReplenishmentList(
      status,
      response => {
        if (response.status === 200) {
          setReplenishments(response.data)
          setFilteredReplenishments(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения пополнений: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      }
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchReplenishments()
  }

  const getRowClazz = (status: string) => {
    switch (status) {
      case "DOC":
      case "CR_REQ":
      case "BILL_PAID":
      case "CR_FIX":
        return "global-item-active"
      case "DONE":
        return "global-item-done"
      default:
        return "global-item"
    }
  }

  const getStatusClazz = (status: string) => {
    switch (status) {
      case "CR_NAC":
      case "DOC_NAC":
        return "status-no"
      case "DOC":
      case "CR_REQ":
      case "BILL_PAID":
        return "status-ok"
      default:
        return "status"
    }
  }

  const confirmRemove = (e: boolean | undefined, id: number) => {
    if (e) {
      remove(id)
    }
  }

  const remove = (id: number) => {
    TrinityAPI.deleteReplenishment(id, response => {
      if (response.status === 200) {
        fetchReplenishments()
      }
    })
  }

  useEffect(() => {
    fetchReplenishments()
  }, [status])

  useEffect(() => {
    if (!search) {
      setFilteredReplenishments(replenishments)
      return
    }

    setFilteredReplenishments(
      replenishments.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.country && r.country.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.invoiceAmount && round2Digits(Number(r.invoiceAmount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.clientName && r.clientName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.clientCompanyName && r.clientCompanyName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalConfirm message={confirmMessage} setMessage={() => setConfirmMessage(undefined)} />
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      {userStore.isManager ? (
        <MenuManager active={"currency-replenishment"} />
      ) : (
        <MenuAdmin active={"currency-replenishment"} />
      )}
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Пополнения валютные</h1>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.deals} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" onClick={refetchData} loading={isRefetchLoading}>
                Обновить список пополнений
              </Button>
            </div>
          </section>

          <section className="search-block">
            <Input
              type="search"
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, компании, имени клиента, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item width-130">Сумма инвойса</li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item cols-deals min-width-150">Страна</li>
                <li className="header-global-item cols-deals">Плательщик</li>
                <li className="header-global-item width-130" style={{ paddingLeft: "10px" }}>
                  Сумма к зачислению
                </li>
                <li className="header-global-item width-130">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option value="">Статус</option>
                    <option value="ACTION_REQUIRED">Требуется действие</option>
                    {Object.keys(ReplenishmentStatus).map((key, index) => (
                      <option value={key} key={index}>
                        {ReplenishmentStatus[key as keyof IReplenishmentStatus][0]}
                      </option>
                    ))}
                  </select>
                </li>
                {userStore.isAdmin && <li className="header-global-item width-115 text-end">Действия</li>}
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredReplenishments.length > 0 ? (
                    filteredReplenishments.map((r, index) => (
                      <li className={getRowClazz(r.status)} id="deals-info-open" key={index}>
                        <div
                          className="global-scroll-block"
                          onClick={() => window.open("/currency-replenishment/" + r.id, "_blank")}
                        >
                          <p className="global-item-text width-130">
                            <span>№{r.id}</span>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: getMskTime(r.createdAt || "") || ""
                              }}
                            />
                          </p>
                          <p className="global-item-text width-130">
                            {r.invoiceAmount ? spacedNum(round2Digits(Number(r.invoiceAmount) / 100)) : "-"}
                          </p>
                          <p className="global-item-text globak-num width-100">{r.currency || "-"}</p>
                          <p className="global-item-text cols-deals min-width-150">
                            <span className="deal-name">{r.country?.toUpperCase() || "-"}</span>
                          </p>
                          <p className="global-item-text cols-deals">
                            <span className="deal-name">{r.currencyPayer || "-"}</span>
                          </p>
                          <li
                            className="global-item-text width-130"
                            style={{ paddingLeft: r.creditedAmount ? 0 : "10px" }}
                          >
                            {r.creditedAmount ? spacedNum(round2Digits(Number(r.creditedAmount) / 100)) : "-"}
                          </li>
                          <p className={`${getStatusClazz(r.status)} width-130`}>
                            {r.status ? ReplenishmentStatus[r.status as keyof IReplenishmentStatus][0] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(r.id?.toString(), r.userId)}>
                          {getUnreadCounter(r.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(r.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                        {userStore.isAdmin && (
                          <div className="header-global-item width-115 buttons-end">
                            <Button
                              variant="black"
                              onClick={() =>
                                setConfirmMessage({
                                  title: "Удалить пополнение?",
                                  text: "Вы действительно желаете удалить пополнение?",
                                  callbackValue: true,
                                  _callback: (val: any) => confirmRemove(val, r.id)
                                })
                              }
                            >
                              Удалить
                            </Button>
                          </div>
                        )}
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Пополнений не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
